/**
 * The token-based login.
 *
 * The existing LoginView mints its own "token" with
 * btoa(JSON.stringify({userId, roleId, organizationId})) and the server never
 * checks it. These calls get a real signed JWT from the server instead, which is
 * what makes the tenant boundary and the licence enforceable.
 *
 * Storage: the access token goes under `saas_access_token`, separate from the
 * legacy `token` entry, so both logins can coexist while the app is migrated
 * screen by screen.
 */

import { getApiGatewayUrl } from '../../config.js'

export const ACCESS_TOKEN_KEY = 'saas_access_token'
export const ACCESS_TOKEN_EXPIRY_KEY = 'saas_access_token_expires'

export interface RoleOption {
  RoleId: number
  RoleName: string
  DeptRoleMapId: number
  SubDepartmentId: number | null
  DepartmentId: number | null
  OrganizationId: number
}

export interface AuthUser {
  UserId: number
  UserName: string
  EmailId: string
  EmployeeCode: string | null
  OrganizationId: number
  OrganizationName: string
  Slug: string
  IsAdmin: boolean
  RoleId: number
  RoleName: string
  DeptRoleMapId: number
  SubDepartmentId: number | null
  DepartmentId: number | null
  BranchId: number | null
  BranchName: string | null
  MustChangePassword: boolean
}

export interface LoginResult {
  Status:
    | 'SUCCESS'
    | 'ROLE_SELECTION_REQUIRED'
    | 'INVALID_CREDENTIALS'
    | 'ACCOUNT_LOCKED'
    | 'ACCOUNT_INACTIVE'
    | 'NO_ROLE_ASSIGNED'
  Message: string
  AccessToken: string | null
  ExpiresAtUtc: string | null
  SelectionTicket: string | null
  Roles: RoleOption[]
  User: AuthUser | null
  Subscription: unknown | null
}

function apiRoot(): string {
  const base = getApiGatewayUrl() || import.meta.env.VITE_API_URL || ''
  return `${String(base).replace(/\/$/, '')}/PlatformCoreServices/ONEHR`
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${apiRoot()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  const envelope = await response.json()

  if (envelope?.ErrorInfo?.ErrorMessage) {
    throw new Error(envelope.ErrorInfo.ErrorMessage)
  }

  return envelope.result as T
}

export const saasAuthService = {
  /** Verifies a credential. May return a role-selection ticket instead of a token. */
  async login(emailOrCode: string, password: string, roleId?: number): Promise<LoginResult> {
    const result = await post<LoginResult>('/Auth/Login', {
      EmailOrCode: emailOrCode,
      Password: password,
      RoleId: roleId ?? null,
    })

    if (result?.Status === 'SUCCESS' && result.AccessToken) {
      saasAuthService.storeToken(result.AccessToken, result.ExpiresAtUtc)
    }

    return result
  },

  /** Exchanges a role-selection ticket for an access token. */
  async selectRole(selectionTicket: string, roleId: number): Promise<LoginResult> {
    const result = await post<LoginResult>('/Auth/SelectRole', {
      SelectionTicket: selectionTicket,
      RoleId: roleId,
    })

    if (result?.Status === 'SUCCESS' && result.AccessToken) {
      saasAuthService.storeToken(result.AccessToken, result.ExpiresAtUtc)
    }

    return result
  },

  storeToken(token: string, expiresAtUtc: string | null): void {
    try {
      localStorage.setItem(ACCESS_TOKEN_KEY, token)
      if (expiresAtUtc) localStorage.setItem(ACCESS_TOKEN_EXPIRY_KEY, expiresAtUtc)
    } catch {
      // Storage can throw in a private window. The token still works for this
      // page; it simply will not survive a reload.
    }
  },

  getToken(): string | null {
    try {
      return localStorage.getItem(ACCESS_TOKEN_KEY)
    } catch {
      return null
    }
  },

  clearToken(): void {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY)
      localStorage.removeItem(ACCESS_TOKEN_EXPIRY_KEY)
    } catch {
      /* ignored */
    }
  },

  /** True when a token is held and has not expired. */
  hasValidToken(): boolean {
    const token = saasAuthService.getToken()
    if (!token) return false

    try {
      const expiry = localStorage.getItem(ACCESS_TOKEN_EXPIRY_KEY)
      if (expiry) {
        const expiresAt = new Date(expiry).getTime()
        // Treat an unparseable date as valid rather than logging the user out
        // over a formatting problem.
        if (!Number.isNaN(expiresAt) && expiresAt <= Date.now()) return false
      }
    } catch {
      /* fall through */
    }

    return true
  },
}

export default saasAuthService
